// WidgetCard, MetricCard and Field, adapted from design/isomorphic/src/core/cards.tsx (itself
// from isomorphic-core/src/components/cards): a bordered card with a display-font title and an
// optional action, a stat card with an icon tile, and a label/value pair for detail cards.
import type { ReactNode } from "react";

import { cn } from "./cn";

export const cardClass = "rounded-xl border border-muted bg-gray-0 dark:bg-gray-50";

export function WidgetCard({
  title,
  description,
  action,
  className,
  children,
  label,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  children?: ReactNode;
  /** The accessible name of the section when the title is not plain text. */
  label?: string;
}) {
  return (
    <section className={cn(cardClass, "p-5 lg:p-6", className)} aria-label={label ?? (typeof title === "string" ? title : undefined)}>
      {title || action ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {title ? <h3 className="text-base font-semibold text-gray-900">{title}</h3> : null}
            {description ? <div className="mt-0.5 text-[13px] text-gray-500">{description}</div> : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function MetricCard({
  title,
  metric,
  icon,
  iconClassName,
  info,
  chart,
  className,
}: {
  title: string;
  metric: ReactNode;
  icon?: ReactNode;
  iconClassName?: string;
  info?: ReactNode;
  chart?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(cardClass, "p-5", className)} role="group" aria-label={title}>
      <div className="flex items-center gap-3">
        {icon ? (
          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 [&>svg]:h-5 [&>svg]:w-5", iconClassName)}>
            {icon}
          </div>
        ) : null}
        <div className="min-w-0 text-[13px] font-medium leading-snug text-gray-500">{title}</div>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="tabular font-display text-[26px] font-extrabold leading-none tracking-tight text-gray-900">{metric}</div>
        {chart ? <div className="h-10 w-24 shrink-0">{chart}</div> : null}
      </div>
      {info ? <div className="mt-3 text-[13px] text-gray-500">{info}</div> : null}
    </div>
  );
}

/** A label and value pair, used in detail cards. */
export function Field({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className={cn("mt-1 truncate text-sm font-medium text-gray-900", mono && "font-mono text-[13px]")}>{children}</dd>
    </div>
  );
}

/** A coloured icon tile: the leading mark of notices and list rows. */
export function IconTile({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg [&>svg]:h-5 [&>svg]:w-5", className)}>{children}</span>
  );
}
